import React from 'react';
import { Flex } from 'app/components/common/Flex';
import NetworkMonitoringFlows from './NetworkMonitoringFlows';
import AppContext from 'app/common/context/AppContext';
import { NetworkMonitoring, NetworkMonitoringProps, NetworkMonitoringState } from './NetworkMonitoring';
import NetworkMonitoringHeader from './NetworkMonitoringHeader';
import { ConcernedTVSection, ConcernedTVSettings, DEFAULT_CONCERNED_TV_SETTINGS } from './flows/ConcernedTVSection';
import Utils from 'app/common/util/Utils';
import {
    ExtendedMonitoredTv,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    MonitoredRegulationOutlook,
} from 'app/model/SOL49';
import memoizeOne from 'memoize-one';
import { CountType, FlightLevelBand, HotspotDto, McdmState, TimePeriod } from 'app/model/CORE';
import NetworkMonitoringMap from 'app/components/dashboard/network/NetworkMonitoringMap';
import { MonitorableMapTv } from 'app/components/map/util/MapUtils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { MeasuresSettings } from 'app/components/dashboard/detailed/measures/MeasuresSettings';
import AppConstants, { NumberOfFlowColumns } from '../../../common/context/AppConstants';

interface NmocNetworkMonitoringProps extends NetworkMonitoringProps {
    showMap: boolean;
    onShowMapChanged?(showMap: boolean): void;
    fullscreenMap: boolean;
    onFullscreenMapChanged?(fullscreenMap: boolean): void;

    //required for fullscreen map only
    monitorableTvs?: Array<MonitorableMapTv>;
    selectedMeasures: Array<MonitoredMeasure>;

    displayedMeasures: Array<MonitoredMeasure>;
    hotspots: { [key: string]: Array<HotspotDto> };
    selectedHotspots: Array<ExtendedMonitoredTv>;
    regulationOutlook: Array<MonitoredRegulationOutlook>;
    selectedRegulationOutlook: Array<MonitoredRegulationOutlook>;

    onDisplayChanged(newDisplay: CountType): void;
    onFlightLevelFilterChanged(flRange: FlightLevelBand): void;
    onMapRefTimePeriodChanged(newPeriod: TimePeriod): void;
}

interface NmocNetworkMonitoringState extends NetworkMonitoringState {
    concernedTvSettings: ConcernedTVSettings;
}

export class NmocNetworkMonitoring extends NetworkMonitoring<NmocNetworkMonitoringProps, NmocNetworkMonitoringState> {
    state = {
        concernedTvSettings: DEFAULT_CONCERNED_TV_SETTINGS,
    };

    getNbColumns = (): NumberOfFlowColumns => {
        return this.props.showMap ? AppConstants.nbFlowsColumnsWithMap : AppConstants.nbFlowsColumns;
    };

    getPendingMeasureProposals = memoizeOne((monitoredMeasureProposals: MonitoredMcdmMeasure[]) => {
        // TODO consider 'proposalFeedback' state
        return monitoredMeasureProposals?.filter(proposal => proposal.mcdmMeasureTopic.state === McdmState.PROPOSED);
    });

    renderConcernedTvs = () => {
        if (!this.props.filteredTvIds) return undefined;

        const filteredTvs =
            this.props.filteredTvIds &&
            this.props.monitoredTvs?.filter(monitoredTv => {
                return Utils.contains(this.props.filteredTvIds, Utils.toUpperCase(monitoredTv.refLocation.id));
            });

        const filteredMeteoAlerts =
            this.props.tvMeteoAlerts &&
            Object.fromEntries(
                Object.entries(this.props.tvMeteoAlerts).filter(([tv]) => this.props.filteredTvIds?.includes(tv)),
            );

        return (
            <ConcernedTVSection
                currentTime={this.props.currentTime}
                tvs={filteredTvs}
                tvSetMap={this.props.tvSetMap}
                monitoredMeasures={this.props.monitoredMeasures}
                pendingMeasureProposals={this.getPendingMeasureProposals(this.props.monitoredMcdmMeasures)}
                tvsOccupancyPeriods={this.props.tvsOccupancyPeriods}
                nbColumns={this.getNbColumns()}
                selectedTvs={this.props.selectedTvs}
                onSelectionChange={this.handleSelectionChange}
                settings={this.state.concernedTvSettings}
                onSettingsChange={settings => this.setState({ concernedTvSettings: settings })}
                meteoAlerts={filteredMeteoAlerts}
                events={this.props.monitoredEvents}
                hotspots={this.props.hotspots}
            />
        );
    };

    renderNetworkMonitoringFlows = () => {
        if (!AppContext.configData) return;

        return (
            <Flex className="overflow" direction="vertical" style={{ overflowX: 'hidden' }}>
                <NetworkMonitoringFlows
                    ref={this.flowsComponentRef}
                    currentTime={this.props.currentTime}
                    monitoredTvs={this.props.monitoredTvs}
                    monitoredMeasures={this.props.monitoredMeasures}
                    pendingMeasureProposals={this.getPendingMeasureProposals(this.props.monitoredMcdmMeasures)}
                    tvSetMap={this.props.tvSetMap}
                    tvSetProgressMap={this.props.tvSetProgressMap}
                    tvSetlastUpdate={this.props.tvSetlastUpdate}
                    tvsOccupancyPeriods={this.props.tvsOccupancyPeriods}
                    nbColumns={this.getNbColumns()}
                    textFilter={this.props.globalFilterEnabled ? this.props.textFilter : ''}
                    selectedTvs={this.props.selectedTvs}
                    onSelectionChange={this.handleSelectionChange}
                    tvMeteoAlerts={this.props.tvMeteoAlerts}
                    monitoredEvents={this.props.monitoredEvents}
                    hotspots={this.props.hotspots}
                />
            </Flex>
        );
    };

    getFilteredMeasures = memoizeOne(
        (measures: MonitoredMeasure[], textFilter: string, settings: MeasuresSettings, monitoredPeriod: TimePeriod) => {
            return MeasureUtils.getFilteredMeasures(measures, textFilter, settings, monitoredPeriod);
        },
    );

    render() {
        const monitoredMeasuresBySettings = this.getFilteredMeasures(
            this.props.displayedMeasures,
            this.props.textFilter,
            this.props.measureSettings,
            this.props.monitoredPeriod,
        );
        const hotspots = this.props.hotspots && Object.values(this.props.hotspots).flat();
        return (
            <Flex className="network-monitoring-panel" direction="vertical">
                <Flex size="5vh">
                    <NetworkMonitoringHeader
                        currentTime={this.props.currentTime}
                        monitoredPeriod={this.props.monitoredPeriod}
                        monitoredMcdmMeasures={this.props.monitoredMcdmMeasures}
                        coordinationSummaries={this.props.coordinationSummaries}
                        selectedTvs={this.props.selectedTvs}
                        tvSetMap={this.props.tvSetMap}
                        showMap={this.props.showMap}
                        fullscreenMap={this.props.fullscreenMap}
                        onFullscreenMapChanged={this.props.onFullscreenMapChanged}
                        onShowMapChanged={this.props.onShowMapChanged}
                        onTextFilterChange={this.props.onTextFilterChange}
                        onSelectAll={this.handleSelectAll}
                        onClearSelection={this.handleClearSelection}
                        globalFilterEnabled={this.props.globalFilterEnabled}
                        onGlobalFilterEnabledChanged={this.props.onGlobalFilterEnabledChanged}
                    />
                </Flex>
                <Flex direction="horizontal" style={{ overflow: 'scroll', margin: '2em 5.5em 2em 2em' }}>
                    <ConditionalRender condition={!(this.props.fullscreenMap && this.props.showMap)}>
                        <Flex
                            direction="vertical"
                            size={this.props.showMap ? `calc(100% - ${AppConstants.miniMapWidth})` : '100%'}
                            style={{ display: this.props.showMap && this.props.fullscreenMap ? 'none' : 'flex' }}>
                            {this.renderConcernedTvs()}
                            {this.renderNetworkMonitoringFlows()}
                        </Flex>
                    </ConditionalRender>
                    <Flex
                        size={
                            this.props.showMap ? (this.props.fullscreenMap ? '100%' : AppConstants.miniMapWidth) : '0%'
                        }
                        style={{ display: this.props.showMap ? 'flex' : 'none' }}>
                        <NetworkMonitoringMap
                            fullscreenMap={this.props.fullscreenMap}
                            currentTime={this.props.currentTime}
                            selectedTvs={this.props.selectedTvs}
                            selectedFlights={this.props.selectedFlights}
                            monitoredTvs={this.props.monitoredTvs}
                            visible={this.props.showMap}
                            convectionData={this.props.convectionData}
                            // airspaceDef={this.props.convectionAirspaceDef}
                            mapSettings={this.props.mapSettings}
                            onMapSettingsChanged={this.props.onMapSettingsChanged}
                            monitoredPeriod={this.props.monitoredPeriod}
                            monitoredMeasures={monitoredMeasuresBySettings}
                            regulationOutlook={this.props.regulationOutlook}
                            selectedRegulationOutlook={this.props.selectedRegulationOutlook}
                            hotspots={hotspots}
                            selectedHotspots={this.props.selectedHotspots}
                            selectedMeasures={this.props.selectedMeasures}
                            textFilter={this.props.globalFilterEnabled ? this.props.textFilter : undefined}
                            tvSetMap={this.props.tvSetMap}
                            dashboardDataVersion={this.props.dashboardDataVersion}
                            filteredTvIds={this.props.filteredTvIds}
                            onDisplayChanged={this.props.onDisplayChanged}
                            onFlightLevelFilterChanged={this.props.onFlightLevelFilterChanged}
                            onMapRefTimePeriodChanged={this.props.onMapRefTimePeriodChanged}
                        />
                    </Flex>
                </Flex>
            </Flex>
        );
    }
}
